/**
 * blogFromTopic — "generate a blog from a topic" for the Live SEO editor.
 *
 * The seam mirrors rewriteMission: the caller injects a `WorkerPort`
 * (production adapts `agent/argoWorker`; tests inject fakes). When the
 * worker answers with a parseable draft it wins (`source: 'worker'`);
 * when it is not configured, throws, or replies with garbage, the
 * DETERMINISTIC fallback draft is used and the result carries an HONEST
 * `note` saying exactly why — the UI never fakes a Claude draft.
 *
 * The fallback is deliberately AEO-shaped so the generated scaffold clears
 * the answer-engine scoring branch of `seoScore.detectAEO`:
 *   - question-shaped H2s (`What is …?`, `How does …?`),
 *   - each answered FIRST in ≤ AEO_ANSWER_WORD_LIMIT words,
 *   - list structure for snippet extraction,
 *   - primary keyword woven to its minimum coverage, secondaries once.
 *
 * PURE except `generateBlogFromTopic`'s single injected side effect
 * (`port.rewrite`). No store, no firebase, no clock, no randomness —
 * same request, same draft, always.
 */

import {
  AEO_ANSWER_WORD_LIMIT,
  META_CHAR_MAX,
  META_CHAR_MIN,
  TITLE_CHAR_MAX,
  scoreContent,
  type KeywordTarget,
  type ScoreInput,
  type SeoScoreResult,
  type SubScore,
} from './seoScore';
import type { WorkerPort } from './rewriteMission';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

/** One topic → blog request. Facts come ONLY from the caller. */
export interface BlogTopicRequest {
  topic: string;
  targets: KeywordTarget[];
  brandFacts?: ScoreInput['brandFacts'];
}

/** The three fields the live editor holds — drop-in for its state. */
export interface BlogDraft {
  title: string;
  meta: string;
  markdown: string;
}

/** The full result of one generation, scored by the SAME engine the
 *  editor lints with, so the toast and the panel can never disagree. */
export interface BlogGenerationResult {
  draft: BlogDraft;
  source: 'worker' | 'fallback';
  /** Honest human-readable provenance ("why this source"). */
  note: string;
  score: SeoScoreResult;
  /** The engine's aeo SubScore, surfaced for the answer-engine readout. */
  aeo: SubScore;
}

// ---------------------------------------------------------------------------
// Small deterministic text helpers
// ---------------------------------------------------------------------------

function words(text: string): string[] {
  return (text ?? '').split(/\s+/).filter((w) => w.length > 0);
}

/** First-letter capitalization, nothing clever. */
function cap(text: string): string {
  const t = (text ?? '').trim();
  return t.length === 0 ? t : t[0].toUpperCase() + t.slice(1);
}

/** Cap a phrase at `n` words so embedded topics can't blow answer limits. */
function firstWords(text: string, n: number): string {
  return words(text).slice(0, n).join(' ');
}

/** Trim to a char bound at a word boundary (never mid-word when avoidable). */
function trimToChars(text: string, charMax: number): string {
  const t = (text ?? '').trim();
  if (t.length <= charMax) return t;
  let cut = t.slice(0, charMax);
  const lastSpace = cut.lastIndexOf(' ');
  if (lastSpace > charMax * 0.5) cut = cut.slice(0, lastSpace);
  return cut.replace(/[\s:,;—–-]+$/, '');
}

// ---------------------------------------------------------------------------
// buildBlogPrompt — the worker contract. PURE + deterministic.
// ---------------------------------------------------------------------------

/**
 * Compose the worker prompt for one topic → blog run. Same request →
 * byte-identical prompt. Carries the brand facts, keyword targets, the
 * AEO answer-first contract, and the exact TITLE/META/BODY reply format
 * `parseWorkerBlog` expects.
 */
export function buildBlogPrompt(req: BlogTopicRequest): string {
  const bf = req.brandFacts;
  return [
    'Write ONE long-form blog post as markdown. Reply in EXACTLY this format:',
    'TITLE: <SERP title, 30-60 characters>',
    `META: <meta description, ${META_CHAR_MIN}-${META_CHAR_MAX} characters>`,
    'BODY:',
    '<the full article markdown, starting with a single # H1>',
    '',
    `TOPIC: ${req.topic.trim()}`,
    '',
    'TARGET TERMS (weave each in naturally, within its use range):',
    ...(req.targets.length > 0
      ? req.targets.map((t) => `- ${t.term} (${t.kind}, ${t.minUses}–${t.maxUses} uses)`)
      : ['- (none provided)']),
    '',
    'BRAND BRAIN:',
    `Voice (write in this register): ${bf && bf.voiceTerms.length > 0 ? bf.voiceTerms.join(', ') : '(none provided)'}`,
    `Banned terms (never use any of these): ${bf && bf.bannedTerms.length > 0 ? bf.bannedTerms.join(', ') : '(none provided)'}`,
    'Proof points (the ONLY claims you may assert — never invent facts):',
    ...(bf && bf.proofPoints.length > 0
      ? bf.proofPoints.map((p) => `- ${p}`)
      : ['- (none provided)']),
    '',
    'SEO + AEO (answer-engine) CONTRACT:',
    '1. Use question-shaped H2s ("What is …?", "How does … work?").',
    `2. Answer FIRST: the paragraph directly under every question H2 must be at most ${AEO_ANSWER_WORD_LIMIT} words.`,
    '3. Include at least one list (bullets or numbered steps).',
    '4. Include at least one internal link to a related page (markdown, /path target).',
    '5. Never invent statistics, customers, or quotes.',
  ].join('\n');
}

// ---------------------------------------------------------------------------
// parseWorkerBlog — untrusted worker text → BlogDraft | null. PURE.
// ---------------------------------------------------------------------------

/** First non-heading, non-list prose paragraph of a markdown body. */
function firstProseParagraph(markdown: string): string {
  const para: string[] = [];
  for (const line of (markdown ?? '').split('\n')) {
    const t = line.trim();
    if (/^#{1,6}\s/.test(t) || /^([-*+]|\d+\.)\s/.test(t)) {
      if (para.length > 0) break;
      continue;
    }
    if (t.length === 0) {
      if (para.length > 0) break;
      continue;
    }
    para.push(t);
  }
  return para.join(' ');
}

/**
 * Parse a worker reply into a draft. Two accepted shapes:
 *   1. the explicit `TITLE: / META: / BODY:` contract from buildBlogPrompt,
 *   2. plain markdown opening with an `# H1` (title lifted, meta derived
 *      from the first prose paragraph — honestly, never invented).
 * Anything else → null, so the caller falls back instead of fabricating.
 */
export function parseWorkerBlog(text: string): BlogDraft | null {
  const raw = (text ?? '').trim();
  if (raw.length === 0) return null;

  const titleMatch = raw.match(/(?:^|\n)\s*TITLE:\s*([^\n]+)/i);
  const metaMatch = raw.match(/(?:^|\n)\s*META:\s*([^\n]+)/i);
  const bodyMatch = raw.match(/(?:^|\n)\s*BODY:\s*\n?([\s\S]+)$/i);
  if (titleMatch && bodyMatch) {
    const title = titleMatch[1].trim();
    const markdown = bodyMatch[1].trim();
    if (title.length > 0 && markdown.length > 0) {
      const meta = (metaMatch?.[1] ?? '').trim();
      return {
        title,
        meta: meta.length > 0 ? meta : trimToChars(firstProseParagraph(markdown), META_CHAR_MAX),
        markdown,
      };
    }
  }

  // Plain-markdown shape: needs a real H1 to be usable as an article.
  const h1 = raw.match(/^#\s+(.+)$/m);
  if (h1) {
    return {
      title: h1[1].trim(),
      meta: trimToChars(firstProseParagraph(raw), META_CHAR_MAX),
      markdown: raw,
    };
  }
  return null;
}

// ---------------------------------------------------------------------------
// fallbackBlogDraft — the always-works offline scaffold. PURE.
// ---------------------------------------------------------------------------

/**
 * Deterministic AEO-shaped scaffold from the topic + targets. Not prose a
 * human would ship verbatim — it is an honest, structurally CORRECT start
 * (question H2s, ≤ limit answers, list, keyword coverage) that the editor's
 * fix cards and section rewrites then improve.
 */
export function fallbackBlogDraft(req: Omit<BlogTopicRequest, 'brandFacts'> & Partial<Pick<BlogTopicRequest, 'brandFacts'>>): BlogDraft {
  const topicRaw = req.topic.trim();
  const primaryTarget = req.targets.find((t) => t.kind === 'primary');
  const primary = firstWords(primaryTarget?.term ?? topicRaw, 8) || 'this topic';
  const topic = firstWords(topicRaw, 12) || primary;
  const secondaries = req.targets
    .filter((t) => t.kind !== 'primary')
    .map((t) => firstWords(t.term, 6));

  // Title: primary up front, inside the SERP char bound.
  const title = trimToChars(`${cap(primary)}: What It Is and How to Start`, TITLE_CHAR_MAX);

  // Meta: build up past the floor, then trim to the ceiling.
  let meta = `An answer-first guide to ${topic}: what ${primary} is, why it matters, and how to put it to work.`;
  if (meta.length < META_CHAR_MIN) meta += ' Includes step-by-step next moves and FAQ answers.';
  if (meta.length < META_CHAR_MIN) meta += ' Written to be lifted by answer engines.';
  meta = trimToChars(meta, META_CHAR_MAX);

  const proof = req.brandFacts?.proofPoints?.[0]
    ? firstWords(req.brandFacts.proofPoints[0], 22)
    : '';

  const secondarySentence =
    secondaries.length > 0
      ? `It connects ${secondaries.join(', ')} into one repeatable workflow.`
      : 'It turns scattered effort into one repeatable workflow.';

  const whyAnswer =
    proof.length > 0
      ? `Because results compound: ${proof}. Teams that lead with ${primary} answer faster and lose less to rework.`
      : `Because results compound. Teams that lead with ${primary} answer faster, decide sooner, and lose less time to rework.`;

  const markdown = [
    `# ${cap(primary)} — a practical, answer-first guide`,
    '',
    `${cap(topic)} is easier when the structure does the work. This guide covers what ${primary} is, how it works, and the first steps to take.`,
    '',
    `## What is ${primary}?`,
    '',
    `${cap(primary)} is the practice behind ${topic} — naming the question your reader actually asks and answering it directly before anything else.`,
    '',
    `## How does ${primary} work?`,
    '',
    `${secondarySentence} Each section leads with the answer, then earns depth with specifics.`,
    '',
    '1. Name the exact question your reader asks.',
    `2. Answer it in the first ${AEO_ANSWER_WORD_LIMIT} words.`,
    '3. Expand with specifics, then link the next step.',
    '',
    `## Why does ${primary} matter?`,
    '',
    whyAnswer,
    '',
    `## How do you get started with ${primary}?`,
    '',
    `Start small: pick one page about ${topic}, restructure it with question headings and direct answers, and measure before rewriting anything else.`,
    '',
    'See also: [related guide](/blog/related-guide).',
  ].join('\n');

  return { title, meta, markdown };
}

// ---------------------------------------------------------------------------
// scoreBlogDraft — one engine, one truth. PURE.
// ---------------------------------------------------------------------------

/**
 * Score a draft with the SAME `scoreContent` engine the live editor lints
 * with, and surface the aeo SubScore by reference (never recomputed in
 * parallel) so the generation toast and the panel breakdown always agree.
 */
export function scoreBlogDraft(
  draft: BlogDraft,
  req: BlogTopicRequest,
): { score: SeoScoreResult; aeo: SubScore } {
  const input: ScoreInput = {
    title: draft.title,
    meta: draft.meta,
    markdown: draft.markdown,
    targets: req.targets,
  };
  if (req.brandFacts) input.brandFacts = req.brandFacts;
  const score = scoreContent(input);
  // The engine always emits all eight dimensions; guard stays honest anyway.
  const aeo =
    score.subScores.find((s) => s.dimension === 'aeo') ??
    ({ dimension: 'aeo', score: 0, max: 100, weight: 0, findings: [] } as SubScore);
  return { score, aeo };
}

// ---------------------------------------------------------------------------
// generateBlogFromTopic — the single side-effecting entry point
// ---------------------------------------------------------------------------

/**
 * Generate a blog draft for a topic. TOTAL by contract — never rejects:
 * worker success → its draft; worker null (not configured) / throw /
 * unusable reply → deterministic fallback, each with an honest `note`.
 */
export async function generateBlogFromTopic(
  req: BlogTopicRequest,
  port: WorkerPort,
): Promise<BlogGenerationResult> {
  let draft: BlogDraft | null = null;
  let source: BlogGenerationResult['source'] = 'fallback';
  let note = '';

  try {
    const out = await port.rewrite(buildBlogPrompt(req));
    if (out === null) {
      note = 'Agent-worker not configured — deterministic AEO outline generated locally.';
    } else {
      const parsed = parseWorkerBlog(out.text);
      if (parsed) {
        draft = parsed;
        source = 'worker';
        note = 'Drafted by the Claude agent-worker.';
      } else {
        note = 'Worker reply was unusable — deterministic AEO outline used instead.';
      }
    }
  } catch {
    note = 'Agent-worker call failed — deterministic AEO outline used instead.';
  }

  if (!draft) draft = fallbackBlogDraft(req);
  const { score, aeo } = scoreBlogDraft(draft, req);
  return { draft, source, note, score, aeo };
}
